import { Favorite, NoteAlt } from '@mui/icons-material'
import LinkIcon from '@mui/icons-material/Link'
import {
    Box,
    Button,
    IconButton,
    Link,
    Typography,
    useTheme,
} from '@mui/material'
import {
    GridColumnVisibilityModel,
    GridRenderCellParams,
    type GridColDef,
} from '@mui/x-data-grid-pro'
import {
    ColumnsByTemplateIdQuery,
    GetCandidatesForRosterSqlQuery,
    RosterSourceStatus,
    SortIndexField,
} from '__generated__/graphql'
import { formatDate } from 'date-fns'
import React, {
    createContext,
    Dispatch,
    FC,
    SetStateAction,
    useMemo,
    useState,
} from 'react'
import { NameColumnHeader } from 'recruiter/contexts/NameColumnHeader'
import ResumeDownloadButton from 'recruiter/components/ResumeDownloadButton'
import ProcessStatusPill from 'recruiter/components/ProcessStatusPill'
import RecruitingStatusPill from 'recruiter/components/RecruitingStatusPill'
import RosterCandidateNameCell from 'recruiter/components/RosterCandidateNameCell'
import { SourceStatusPill } from 'recruiter/components/SourceStatusPill'
import SystemStatusPill from 'recruiter/components/SystemStatusPill'
import { GetCandidatesForRosterSql } from 'recruiter/contexts'
import { useCandidateSearch } from 'recruiter/hooks'
import ColorsAvatarGroup from 'recruiter/pages/Rosters/RosterProfile/CandidatesTableColors/ColorsAvatarGroup'
import ColorsDropdown from 'recruiter/pages/Rosters/RosterProfile/CandidatesTableColors/ColorsDropdown'
import ProcessStatusEditCell from 'recruiter/pages/Rosters/RosterProfile/EditCells/ProcessStatusEditCell'
import ProcessStatusEditWithAddToRoster from 'recruiter/pages/Rosters/RosterProfile/EditCells/ProcessStatusEditWithAddToRoster'
import SourceStatusEditCell from 'recruiter/pages/Rosters/RosterProfile/EditCells/SourceStatusEditCell'
import { generateRowId } from 'recruiter/components/RosterCandidatesSearchResultsTable'
import { RosterCandidateTableViewId } from 'recruiter/pages/Rosters/RosterProfile/Search/RosterSearchProfile'
import { recruiterRouteConfig } from 'recruiter/routes'
import useToast from 'shared/components/Toast/useToast'
import { isCandidateRecruitingStatus } from 'shared/models/candidateRecruitingStatus'

type RosterCandidateTableColumnDefinition =
    GridColDef<RosterCandidateTableRowModel> & {
        /** The ID of the OpenSearch sorting field to apply when sorting with this column. */
        sortIndexField?: SortIndexField
    }

export interface RosterCandidatesColumnsContextValue {
    columnVisibilityModel: GridColumnVisibilityModel
    setColumnVisibilityModel: Dispatch<
        SetStateAction<GridColumnVisibilityModel>
    >
    onRosterColumnDefs: {
        [key: string]: RosterCandidateTableColumnDefinition
    }
    allColumnDefs: {
        [key: string]: RosterCandidateTableColumnDefinition
    }
    columnDef: RosterCandidateTableColumnDefinition[]
    setColumnDef: Dispatch<
        SetStateAction<RosterCandidateTableColumnDefinition[]>
    >
    columnDefaults: ColumnsByTemplateIdQuery['candidateColumns']
}

export interface RosterCandidatesColumnsProviderProps {
    children: React.ReactNode
    ActionsCell: FC<GridRenderCellParams>
    currentView: RosterCandidateTableViewId
    rosterId: string
    canEdit: boolean
    onOpenNoteDrawer?: (rosterCandidateId: string) => void
}

export const RosterCandidatesColumnsContext =
    createContext<RosterCandidatesColumnsContextValue>(
        {} as RosterCandidatesColumnsContextValue
    )

type RosterCandidateTableRowModel =
    GetCandidatesForRosterSqlQuery['candidateSearchSql']['data'][number]

/**
 * default valueGetter to provide fallback value if the column's "field" is not present in the data
 */
const valueGetter = (value, row) => value ?? '-'

export const RosterCandidateColumnsProvider: React.FC<
    RosterCandidatesColumnsProviderProps
> = ({
    children,
    ActionsCell,
    currentView,
    rosterId,
    canEdit,
    onOpenNoteDrawer,
}) => {
    const toast = useToast()
    const theme = useTheme()
    const [columnVisibilityModel, setColumnVisibilityModel] =
        useState<GridColumnVisibilityModel>({})

    const { setActiveFilters } = useCandidateSearch()

    const sharedColumnDefs = useMemo(() => {
        // displayed for every template
        return {
            name: {
                field: 'name',
                editable: false,
                sortable: false,
                width: 220,
                renderCell: ({ row }) => (
                    <RosterCandidateNameCell
                        row={row}
                        setActiveFilters={() => setActiveFilters([])}
                    />
                ),
                renderHeader: () => <NameColumnHeader />,
            },
            colors: {
                field: 'colors',
                headerName: 'Colors',
                sortable: false,
                editable: canEdit ?? false,
                width: 65,
                renderCell: ({ id, row }) =>
                    row.rosterCandidate ? (
                        <ColorsAvatarGroup
                            colors={
                                row.rosterCandidate?.candidateColors
                                    ?.map(({ rosterColor }) => rosterColor)
                                    .filter((c) => !!c) ?? []
                            }
                            id={id.toString()}
                            disabled={!canEdit}
                        />
                    ) : (
                        '-'
                    ),
                renderEditCell: (params) => {
                    return params.row.rosterCandidate ? (
                        <ColorsDropdown
                            colors={
                                params.row.rosterCandidate?.candidateColors
                                    ?.map(({ rosterColor }) => rosterColor)
                                    .filter((c) => !!c) ?? []
                            }
                            rosterCandidateId={
                                params.row.rosterCandidateId || ''
                            }
                            refetchQueries={[GetCandidatesForRosterSql]}
                            canEdit={canEdit ?? false}
                            {...params}
                        />
                    ) : (
                        '-'
                    )
                },
            },
            rosterCandidateFavoriteCount: {
                field: 'rosterCandidateFavoriteCount',
                headerName: 'Favorited',
                editable: false,
                sortable: false,
                width: 80,
                valueGetter: (value, row) => row?.rosterCandidateFavoriteCount,
                renderCell: ({ row }) =>
                    row.rosterCandidateFavoriteCount > 0 && (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                        >
                            <Favorite fontSize="large" color="error" />
                            <Typography
                                variant="caption"
                                position="absolute"
                                fontWeight="bold"
                                color={theme.palette.common.white}
                            >
                                {row.rosterCandidateFavoriteCount}
                            </Typography>
                        </Box>
                    ),
            },
            actions: {
                field: 'actions',
                headerName: '',
                sortable: false,
                editable: false,
                width: 100,
                renderCell: ActionsCell,
                renderHeader: () => null,
                resizable: false,
            },
            internalNote: {
                field: 'internalNote',
                headerName: 'Internal Note',
                sortable: true,
                sortIndexField: SortIndexField.RosterInternalNote,
                editable: false,
                flex: 1.5,
                minWidth: 150,
                valueGetter: (value, row) =>
                    row.rosterCandidate?.internalRecruiterNote?.note,
                renderCell: ({ row }) => {
                    const note =
                        row.rosterCandidate?.internalRecruiterNote?.note
                    if (!note) return '—'
                    return (
                        <Box
                            display="flex"
                            alignItems="center"
                            gap={0.5}
                            overflow="hidden"
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    lineHeight: 'inherit',
                                    flex: 1,
                                    minWidth: 0,
                                }}
                            >
                                {note}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (
                                        row.rosterCandidateId &&
                                        onOpenNoteDrawer
                                    ) {
                                        onOpenNoteDrawer(row.rosterCandidateId)
                                    }
                                }}
                            >
                                <NoteAlt fontSize="small" />
                            </IconButton>
                        </Box>
                    )
                },
            },
            dateAdded: {
                field: 'dateAdded',
                headerName: 'Date Added',
                editable: false,
                sortable: true,
                sortIndexField: SortIndexField.DateOnRoster,
                width: 120,
                valueGetter: (value, row) =>
                    row.rosterCandidateCreatedAt
                        ? formatDate(row.rosterCandidateCreatedAt, 'MM/dd/yyyy')
                        : '-',
            },
            processStatusUpdatedAt: {
                field: 'processStatusUpdatedAt',
                headerName: 'Process Status Updated At',
                editable: false,
                sortable: true,
                width: 180,
                sortIndexField: SortIndexField.ProcessStatusUpdatedAt,
                valueGetter: (value, row) =>
                    row.rosterCandidate?.candidateProcessStatus?.createdAt
                        ? formatDate(
                              row.rosterCandidate.candidateProcessStatus
                                  .createdAt,
                              'MM/dd/yyyy'
                          )
                        : '-',
            },
            // used for one or more templates
            act: {
                field: 'act',
                valueGetter,
                sortable: false,
                editable: false,
            },
            analystClassYear: {
                field: 'analystClassYear',
                valueGetter,
                sortable: false,
                editable: false,
            },
            candidateProfileUrl: {
                field: 'candidateProfileUrl',
                sortable: false,
                editable: false,
                renderCell: ({ row }) => (
                    <Button
                        onClick={() => {
                            setActiveFilters([])
                        }}
                        href={`${recruiterRouteConfig.candidates.path}/${row.candidateId}`}
                        aria-label="View Candidate Profile"
                        variant="text"
                    >
                        <LinkIcon />
                    </Button>
                ),
            },
            cpiPlaced: {
                field: 'cpiPlaced',
                sortable: false,
                editable: false,
                valueGetter: (value, row) => row?.cpiAttribute ?? '-',
            },
            currentCompany: {
                field: 'company',
                headerName: 'Company',
                editable: false,
                sortable: true,
                sortIndexField: SortIndexField.Company,
                width: 150,
                renderCell: ({ row }) =>
                    row.currentCompany ? (
                        <Link
                            onClick={() => {
                                setActiveFilters([])
                            }}
                            href={`/companies/${row.currentCompanyId}`}
                            sx={{ textDecoration: 'none' }}
                        >
                            {row.currentCompany}
                        </Link>
                    ) : (
                        '-'
                    ),
            },
            ccatRawScore: {
                field: 'ccatRawScore',
                headerName: 'CCAT Raw Score',
                editable: false,
                sortable: false,
                width: 150,
                valueGetter,
            },
            ccatPercentile: {
                field: 'ccatPercentile',
                headerName: 'CCAT Percentile',
                editable: false,
                sortable: false,
                width: 150,
                valueGetter,
            },
            currentIndustry: {
                field: 'industries',
                headerName: 'Industries',
                editable: false,
                sortable: false,
                width: 150,
                renderCell: ({ row }) => {
                    const industryString =
                        row.currentIndustry?.split(';').join(', ') || '-'

                    return (
                        <Box component="span" title={industryString}>
                            {industryString}
                        </Box>
                    )
                },
            },
            publicBio: {
                field: 'publicBio',
                headerName: 'Public Bio',
                editable: false,
                sortable: false,
                width: 150,
                valueGetter,
            },
            currentLocation: { field: 'currentLocation', valueGetter },
            currentSector: {
                field: 'sectors',
                headerName: 'Sectors',
                editable: false,
                sortable: false,
                width: 150,
                renderCell: ({ row }) => {
                    const sectorString =
                        row.currentSector?.split(';').join(', ') || '-'

                    return (
                        <Box component="span" title={sectorString}>
                            {sectorString}
                        </Box>
                    )
                },
            },
            currentSectorDescription: {
                field: 'currentSectorDescription',
                valueGetter,
                sortable: false,
                editable: false,
            },
            currentTitle: {
                field: 'currentTitle',
                valueGetter,
                sortable: false,
                editable: false,
            },
            email: {
                field: 'email',
                valueGetter,
                sortable: false,
                editable: false,
            },
            ethnicity: {
                field: 'ethnicity',
                valueGetter,
                sortable: false,
                editable: false,
            },
            gender: {
                field: 'gender',
                valueGetter,
                sortable: false,
                editable: false,
            },
            gmat: {
                field: 'gmat',
                valueGetter,
                sortable: false,
                editable: false,
            },
            hometown: {
                field: 'hometown',
                valueGetter,
                sortable: false,
                editable: false,
            },
            linkedinUrl: {
                field: 'linkedinUrl',
                sortable: false,
                editable: false,
                renderCell: ({ row }) =>
                    row.linkedinUrl ? (
                        <Link href={`//${row.linkedinUrl}`}>
                            {row.linkedinUrl}
                        </Link>
                    ) : (
                        '-'
                    ),
            },
            mbaEducation: {
                field: 'mbaEducation',
                valueGetter,
                sortable: false,
                editable: false,
            },
            mbaTiering: {
                field: 'mbaTiering',
                valueGetter,
                sortable: false,
                editable: false,
            },
            mbaYear: {
                field: 'mbaYear',
                valueGetter,
                sortable: false,
                editable: false,
            },
            noteForClient: {
                field: 'noteForClient',
                valueGetter,
                sortable: false,
                editable: false,
            },
            phone: {
                field: 'phone',
                valueGetter,
                sortable: false,
                editable: false,
            },
            preAdmitAttribute: {
                field: 'preAdmitAttribute',
                sortable: false,
                editable: false,
                valueGetter: (value, row) =>
                    row?.preAdmitAttribute ? 'Yes' : 'No',
            },
            preferredEmail: {
                field: 'preferredEmail',
                valueGetter,
                sortable: false,
                editable: false,
            },
            previousCompany: {
                field: 'previousCompany',
                valueGetter,
                sortable: false,
                editable: false,
            },
            previousIndustry: {
                field: 'previousIndustry',
                valueGetter,
                sortable: false,
                editable: false,
            },
            previousSector: {
                field: 'previousSector',
                valueGetter,
                sortable: false,
                editable: false,
            },
            previousStartDate: {
                field: 'previousStartDate',
                valueGetter,
                sortable: false,
                editable: false,
            },
            previousTitle: {
                field: 'previousTitle',
                valueGetter,
                sortable: false,
                editable: false,
            },
            recruitingGeoPref1: {
                field: 'recruitingGeoPref1',
                sortable: false,
                editable: false,
                valueGetter: (value, row) =>
                    row.recruitingGeoLocationPref1 || '-',
            },
            recruitingGeoPref2: {
                field: 'recruitingGeoPref2',
                sortable: false,
                editable: false,
                valueGetter: (value, row) =>
                    row.recruitingGeoLocationPref2 || '-',
            },
            recruitingGeoPref3: {
                field: 'recruitingGeoPref3',
                sortable: false,
                editable: false,
                valueGetter: (value, row) =>
                    row.recruitingGeoLocationPref3 || '-',
            },
            recruitingIndustryPref1: {
                field: 'recruitingIndustryPref1',
                sortable: false,
                editable: false,
                valueGetter,
            },
            recruitingIndustryPref2: {
                field: 'recruitingIndustryPref2',
                sortable: false,
                editable: false,
                valueGetter,
            },
            recruitingSectorPref1: {
                field: 'recruitingSectorPref1',
                sortable: false,
                editable: false,
                valueGetter,
            },
            recruitingSectorPref2: {
                field: 'recruitingSectorPref2',
                sortable: false,
                editable: false,
                valueGetter,
            },
            recruitingSectorPref3: {
                field: 'recruitingSectorPref3',
                sortable: false,
                editable: false,
                valueGetter,
            },
            recruitingStatus: {
                field: 'recruitingStatus',
                sortable: false,
                editable: false,
                headerName: 'Recruiting Status',
                renderCell: ({ row }) => (
                    <RecruitingStatusPill
                        status={
                            row.recruitingStatus &&
                            isCandidateRecruitingStatus(row.recruitingStatus)
                                ? row.recruitingStatus
                                : 'No Status'
                        }
                    />
                ),
                width: 150,
            },
            referenceAttribute: {
                field: 'referenceAttribute',
                valueGetter,
                sortable: false,
                editable: false,
            },
            resumeUpdatedDate: {
                field: 'resumeUpdatedDate',
                sortable: false,
                editable: false,
                valueGetter: (value, row) =>
                    value ? formatDate(new Date(value), 'MM/dd/yyyy') : '-',
            },
            resumeUrl: {
                field: 'candidateDocumentId',
                sortable: false,
                editable: false,
                renderCell: ({ row }) =>
                    row.candidateDocumentId ? (
                        <ResumeDownloadButton
                            documentId={row.candidateDocumentId}
                            documentKey={row.candidateDocumentKey}
                        />
                    ) : (
                        '-'
                    ),
            },
            rosterCommentsFromBlast: {
                field: 'rosterCommentsFromBlast',
                sortable: false,
                editable: false,
                valueGetter,
            },
            rosterInterestFromBlast: {
                field: 'rosterInterestFromBlast',
                sortable: false,
                editable: false,
                valueGetter: (value: boolean) =>
                    value === true ? 'Yes' : value === false ? 'No' : '-',
            },
            rosterNotInterestedReasonFromBlast: {
                field: 'rosterNotInterestedReasonFromBlast',
                sortable: false,
                editable: false,
                valueGetter,
            },
            rosterLocationFromBlast: {
                field: 'rosterLocationFromBlast',
                sortable: false,
                editable: false,
                valueGetter: (value, row) =>
                    row?.rosterCandidate?.mostRecentJobBoardResponse?.officePrefs?.map(
                        (op) => op.companyOffice?.officeLocation?.value
                    ) ?? '-',
            },
            rosterSectorFromBlast: {
                field: 'rosterSectorFromBlast',
                sortable: false,
                editable: false,
                valueGetter: (value, row) =>
                    row?.rosterCandidate?.mostRecentJobBoardResponse?.sectorPrefs?.map(
                        (sp) => sp.sector?.value
                    ) ?? '-',
            },
            sat: {
                field: 'sat',
                valueGetter,
                sortable: false,
                editable: false,
            },
            starRatingMostRecentScreen: {
                field: 'starRatingMostRecentScreen',
                sortable: false,
                editable: false,
                valueGetter,
            },
            starRatingReference: {
                field: 'starRatingReference',
                valueGetter,
                sortable: false,
                editable: false,
            },
            startDate: {
                field: 'startDate',
                valueGetter,
                sortable: false,
                editable: false,
            },
            systemStatus: {
                field: 'systemStatus',
                headerName: 'Registration Status',
                editable: false,
                sortable: false,
                width: 150,
                renderCell: ({ row }) => (
                    <SystemStatusPill
                        status={row.systemStatus}
                        statusDate={row.systemStatusDate}
                    />
                ),
            },
            undergradEducation: {
                field: 'college',
                headerName: 'Undergrad College',
                editable: false,
                sortable: false,
                width: 150,
                valueGetter: (value, row) => row.undergradEducation || '-',
            },
            undergradGpa: {
                field: 'undergradGpa',
                valueGetter,
                sortable: false,
                editable: false,
            },
            undergradYear: {
                field: 'undergradYear',
                valueGetter,
                sortable: false,
                editable: false,
            },
            visaStatus: {
                field: 'visaStatus',
                valueGetter,
                sortable: false,
                editable: false,
            },
        }
    }, [
        canEdit,
        ActionsCell,
        setActiveFilters,
        theme.palette.common.white,
        toast,
        onOpenNoteDrawer,
    ])

    const allSpecificColumnDefs = useMemo(() => {
        return {
            processStatus: {
                field: 'processStatus',
                headerName: 'Process Status',
                editable: canEdit ?? false,
                // Disable sorting in the "all candidates" view since process status
                // sorting is only supported when scoped to a specific roster right now.
                sortable: currentView !== 'all',
                sortIndexField:
                    currentView === 'all'
                        ? undefined
                        : SortIndexField.ProcessStatus,
                width: 150,
                renderEditCell: (params) => {
                    if (!canEdit) return '-'
                    if (
                        !params.row.rosterCandidate?.candidateProcessStatus
                            ?.processStatus?.status
                    )
                        return (
                            <ProcessStatusEditWithAddToRoster
                                {...params}
                                canEdit={canEdit ?? false}
                                refetchQueries={[GetCandidatesForRosterSql]}
                                candidateId={params.row.candidateId}
                                rosterId={rosterId}
                            />
                        )
                    return (
                        <ProcessStatusEditCell
                            rosterId={rosterId}
                            canEdit={canEdit ?? false}
                            api={params.api}
                            currentProcessStatus={
                                params.row.rosterCandidate
                                    ?.candidateProcessStatus?.processStatus
                            }
                            refetchQueries={[GetCandidatesForRosterSql]}
                            rosterCandidateId={
                                params.row.rosterCandidateId || ''
                            }
                            rowId={generateRowId(params.row)}
                        />
                    )
                },
                renderCell: (params) => {
                    if (
                        !params.row.rosterCandidate?.candidateProcessStatus
                            ?.processStatus?.status
                    ) {
                        return (
                            <ProcessStatusEditWithAddToRoster
                                {...params}
                                candidateId={params.row.candidateId}
                                refetchQueries={[GetCandidatesForRosterSql]}
                                rosterId={rosterId}
                                onSuccess={(params, rosterCandididates) => {
                                    // when adding a candidate to roster, a row id previously had no rosterCandidateId and now it has both an id and a rosterCandidateId so we need to reselect that row
                                    const selectedRows =
                                        params.api.getSelectedRows()
                                    // deselect the row with the old id
                                    const selectedRowIds = Array.from(
                                        selectedRows.keys()
                                    ).filter(
                                        (id) => id !== params.row.candidateId
                                    )
                                    // select the rows with new ids (only if they were previously selected)
                                    if (
                                        selectedRows.get(params.row.candidateId)
                                    ) {
                                        rosterCandididates.forEach(
                                            ({ id, candidateId }) => {
                                                selectedRowIds.push(
                                                    generateRowId({
                                                        id:
                                                            candidateId ||
                                                            params.row
                                                                .candidateId,
                                                        rosterCandidateId: id,
                                                    })
                                                )
                                            }
                                        )
                                    }
                                    // save the new selected rows to the table
                                    params.api.setRowSelectionModel(
                                        selectedRowIds
                                    )
                                }}
                            />
                        )
                    }
                    return (
                        <ProcessStatusPill
                            processStatusId={
                                params.row.rosterCandidate
                                    ?.candidateProcessStatus?.processStatus?.id
                            }
                        />
                    )
                },
            },
            sourceStatus: {
                field: 'sourceStatus',
                headerName: 'Source Status',
                editable: (canEdit ?? false) && currentView !== 'all',
                sortable: false,
                width: 150,
                renderEditCell: ({ row, api }) => (
                    <SourceStatusEditCell
                        api={api}
                        disabled={!row.rosterCandidate || !(canEdit ?? false)}
                        currentSourceStatus={
                            row.rosterCandidate?.sourceStatus ??
                            RosterSourceStatus.NoStatus
                        }
                        refetchQueries={[GetCandidatesForRosterSql]}
                        rosterCandidateId={row.rosterCandidateId || ''}
                        rowId={generateRowId(row)}
                    />
                ),
                renderCell: ({ row }) => (
                    <SourceStatusPill
                        status={row.rosterCandidate?.sourceStatus}
                    />
                ),
            },
        }
    }, [canEdit, currentView, rosterId])

    const onRosterSpecificColumnDefs = useMemo(() => {
        return {
            processStatus: {
                field: 'processStatus',
                headerName: 'Process Status',
                editable: canEdit ?? false,
                sortable: true,
                sortIndexField: SortIndexField.ProcessStatus,
                width: 150,
                renderEditCell: ({ row, api }) => (
                    <ProcessStatusEditCell
                        rosterId={rosterId}
                        canEdit={canEdit ?? false}
                        api={api}
                        currentProcessStatus={
                            row.rosterCandidate?.candidateProcessStatus
                                ?.processStatus
                        }
                        refetchQueries={[GetCandidatesForRosterSql]}
                        rosterCandidateId={row.rosterCandidateId || ''}
                        rowId={generateRowId(row)}
                    />
                ),
                renderCell: (params) => {
                    return (
                        <ProcessStatusPill
                            processStatusId={
                                params.row.rosterCandidate
                                    ?.candidateProcessStatus?.processStatus?.id
                            }
                        />
                    )
                },
            },
            sourceStatus: {
                field: 'sourceStatus',
                headerName: 'Source Status',
                editable: canEdit ?? false,
                sortable: false,
                width: 150,
                renderEditCell: ({ row, api }) => (
                    <SourceStatusEditCell
                        api={api}
                        disabled={!row.rosterCandidate || !(canEdit ?? false)}
                        currentSourceStatus={
                            row.rosterCandidate?.sourceStatus ??
                            RosterSourceStatus.NoStatus
                        }
                        refetchQueries={[GetCandidatesForRosterSql]}
                        rosterCandidateId={row.rosterCandidateId || ''}
                        rowId={generateRowId(row)}
                    />
                ),
                renderCell: ({ row }) => (
                    <SourceStatusPill
                        status={row.rosterCandidate?.sourceStatus}
                    />
                ),
            },
        }
    }, [canEdit, rosterId])

    const columnDefaults = [
        { internalName: 'name', columnName: 'Name' },
        { internalName: 'dateAdded', columnName: 'Date Added' },
        { internalName: 'processStatus', columnName: 'Process Status' },
        {
            internalName: 'processStatusUpdatedAt',
            columnName: 'Status Updated',
        },
        { internalName: 'internalNote', columnName: 'Internal Note' },
        { internalName: 'currentCompany', columnName: 'Company' },
        { internalName: 'sourceStatus', columnName: 'Source Status' },
        { internalName: 'systemStatus', columnName: 'Registration Status' },
        {
            internalName: 'recruitingStatus',
            columnName: 'Recruiting Status',
        },
    ]

    const allViewColumnDefs = useMemo(() => {
        return { ...sharedColumnDefs, ...allSpecificColumnDefs }
    }, [allSpecificColumnDefs, sharedColumnDefs])
    const onRosterViewColumnDefs = useMemo(() => {
        return {
            ...sharedColumnDefs,
            ...onRosterSpecificColumnDefs,
        }
    }, [onRosterSpecificColumnDefs, sharedColumnDefs])

    // columnDef is stored in state to control order of columns
    const [columnDef, setColumnDef] = useState(
        columnDefaults.map((col) =>
            currentView === 'all'
                ? allViewColumnDefs[col.internalName]
                : onRosterViewColumnDefs[col.internalName]
        )
    )

    return (
        <RosterCandidatesColumnsContext.Provider
            value={{
                columnVisibilityModel,
                setColumnVisibilityModel,
                onRosterColumnDefs: onRosterViewColumnDefs,
                allColumnDefs: allViewColumnDefs,
                columnDef,
                setColumnDef,
                columnDefaults,
            }}
        >
            {children}
        </RosterCandidatesColumnsContext.Provider>
    )
}
